// delPatchQueue.js
// IndexedDB queue management for DEL form autosave and holding websocket requests for offline capabilities

import { openDB } from "idb";
import { withIdbRetry } from "./idbRetry";

const DB_NAME = "delPatchQueue";
const STORE_NAME = "patches";

const getDb = async () => {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: "queueId",
          autoIncrement: true,
        });
        store.createIndex("delId", "delId", { unique: false });
      }
    },
  });
};

export const queuePatch = async (delId, patch) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      return db.add(STORE_NAME, {
        delId,
        patch,
        timestamp: Date.now(),
      });
    },
    undefined,
    "delPatchQueue.queuePatch"
  );
};

export const getQueuedPatches = async (delId) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      const tx = db.transaction(STORE_NAME, "readonly");
      const index = tx.store.index("delId");
      return index.getAll(delId);
    },
    [],
    "delPatchQueue.getQueuedPatches"
  );
};

export const removePatchFromQueue = async (queueId) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      await db.delete(STORE_NAME, queueId);
    },
    undefined,
    "delPatchQueue.removePatchFromQueue"
  );
};

export const getPendingCount = async (delId) => {
  const patches = await getQueuedPatches(delId);
  return patches.length;
};

export const flushQueue = async (delId, emitFn) => {
  const patches = await getQueuedPatches(delId);
  let successCount = 0;
  for (const item of patches) {
    const result = await emitFn(delId, item.patch);
    // Remove from queue if: success OR server rejected it (validation error)
    // Only keep in queue if it should be retried (network issues)
    if (!result.error || !result.shouldQueue) {
      await removePatchFromQueue(item.queueId);
      if (!result.error) {
        successCount++;
      }
    }
  }
  return successCount;
};

export const clearQueue = async (delId) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      const tx = db.transaction(STORE_NAME, "readwrite");
      const index = tx.store.index("delId");
      const keys = await index.getAllKeys(delId);
      for (const key of keys) {
        await tx.store.delete(key);
      }
      await tx.done;
    },
    undefined,
    "delPatchQueue.clearQueue"
  );
};
